import { AlertNotification } from '@taskall/react';
import * as React from 'react';

export type ToastStatus = 'success' | 'error' | 'information' | 'warning';
export type ToastInput = { title: string; body?: string; status?: ToastStatus; durationMs?: number };
type ToastItem = ToastInput & { id: number };

const ToastContext = React.createContext<((t: ToastInput) => void) | null>(null);

/** Toasts com o AlertNotification do design system; região aria-live para leitores de tela. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const seq = React.useRef(0);
  const dismiss = React.useCallback((id: number) => setItems((all) => all.filter((t) => t.id !== id)), []);
  const push = React.useCallback(
    (t: ToastInput) => {
      seq.current += 1;
      const id = seq.current;
      setItems((all) => [...all.slice(-3), { ...t, id }]);
      window.setTimeout(() => dismiss(id), t.durationMs ?? 5000);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-[80] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2"
      >
        {items.map((t) => (
          <AlertNotification
            key={t.id}
            className="pointer-events-auto shadow-[var(--taskall-shadow-lg)]"
            title={t.title}
            body={t.body}
            status={t.status ?? 'success'}
            visualStyle="filled"
            size="small"
            closable
            onClose={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (t: ToastInput) => void {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  return ctx;
}
